import { Braces, Webhook, ShieldCheck, Network, Repeat, TerminalSquare } from "lucide-react";

const CATEGORY = "Entwickler";

export const DEVELOPERS = [
  {
    slug: "apis",
    name: "APIs",
    category: CATEGORY,
    icon: Braces,
    tagline: "REST-first, dokumentiert, ereignisgetrieben.",
    overline: "APIs",
    hero: {
      title: "Eine API für die gesamte Bank.",
      subtitle:
        "REST-first, vollständig typisiert und dokumentiert. Was das Dashboard kann, kann auch Ihr Code – deterministisch, idempotent und versioniert.",
    },
    stats: [
      { value: "REST", label: "OpenAPI 3" },
      { value: "SDKs", label: "TS · Py · Go" },
      { value: "Sandbox", label: "Testdaten" },
    ],
    features: [
      { icon: Braces, title: "REST & OpenAPI", desc: "Konsistente Ressourcen, vollständige OpenAPI-Spezifikation, generierte SDKs." },
      { icon: ShieldCheck, title: "Idempotenz", desc: "Idempotency-Keys machen jeden schreibenden Aufruf sicher wiederholbar." },
      { icon: TerminalSquare, title: "Sandbox", desc: "Vollständige Testumgebung mit realistischen Daten und simulierbaren Events." },
      { icon: Repeat, title: "Versionierung", desc: "Klare Versionierung ohne Breaking Changes – Ihr Code bleibt stabil." },
    ],
    architecture: {
      caption: "API-Layer als einziger Zugangspunkt zur Plattform",
      layers: [
        { label: "Clients", nodes: [{ label: "SDK", sub: "TS/Py/Go" }, { label: "HTTP", sub: "REST" }, { label: "CLI", sub: "Tooling" }] },
        { label: "API-Gateway", nodes: [{ label: "Auth", sub: "Keys" }, { label: "Idempotenz", sub: "Keys" }, { label: "Rate-Limit", sub: "fair" }] },
        { label: "Dienste", nodes: [{ label: "Konten", sub: "" }, { label: "Zahlungen", sub: "" }, { label: "Karten", sub: "" }] },
      ],
    },
    api: {
      method: "GET",
      endpoint: "/v1/accounts/:id",
      code: [
        "// Authentifizierter Request mit Idempotenz",
        "const res = await cashfux.get(\"/v1/accounts/acc_9f2\", {",
        "  headers: {",
        '    "Authorization": `Bearer ${apiKey}`,',
        '    "Idempotency-Key": "req_88d1",',
        "  },",
        "});",
        "res.status // 200",
      ],
      highlights: [
        "OpenAPI 3 mit generierten SDKs",
        "Idempotency-Keys für sichere Retries",
        "Sandbox getrennt von Live per Key",
      ],
    },
    useCases: [
      { title: "Embedded Finance", desc: "Bankfunktionen nativ in Ihr Produkt integrieren." },
      { title: "Automatisierung", desc: "Finanzprozesse vollständig programmatisch steuern." },
      { title: "Schnelle Integration", desc: "Vom ersten Call zur Produktion mit generierten SDKs." },
    ],
    faq: [
      { q: "Welche SDKs gibt es?", a: "Offiziell für TypeScript, Python und Go, generiert aus der OpenAPI-Spezifikation." },
      { q: "Wie funktioniert die Authentifizierung?", a: "Über API-Keys als Bearer-Token, getrennt für Sandbox und Live." },
      { q: "Wie werden Breaking Changes vermieden?", a: "Durch strikte Versionierung – bestehende Versionen bleiben stabil." },
      { q: "Gibt es eine Testumgebung?", a: "Ja, eine vollständige Sandbox mit realistischen Testdaten und simulierbaren Events." },
    ],
    related: ["webhooks", "buchhaltung", "geschaeftskonten"],
  },
  {
    slug: "webhooks",
    name: "Webhooks",
    category: CATEGORY,
    icon: Webhook,
    tagline: "Ereignisse in Echtzeit, sicher signiert.",
    overline: "Webhooks",
    hero: {
      title: "Ihr System erfährt alles zuerst.",
      subtitle:
        "Jedes relevante Ereignis wird als signierter Webhook zugestellt – mit Retries, Idempotenz und vollständigem Event-Log für lückenlose Verarbeitung.",
    },
    stats: [
      { value: "Signiert", label: "HMAC" },
      { value: "Retries", label: "mit Backoff" },
      { value: "Replay", label: "Event-Log" },
    ],
    features: [
      { icon: Webhook, title: "Echtzeit-Events", desc: "Zustandsänderungen von Konten, Zahlungen, Karten und mehr sofort erhalten." },
      { icon: ShieldCheck, title: "Signaturprüfung", desc: "HMAC-Signatur je Event stellt Authentizität und Integrität sicher." },
      { icon: Repeat, title: "Retries & Backoff", desc: "Fehlgeschlagene Zustellungen werden mit exponentiellem Backoff wiederholt." },
      { icon: Network, title: "Event-Log & Replay", desc: "Vollständiges Log mit manuellem Replay einzelner Events." },
    ],
    architecture: {
      caption: "Zuverlässige Event-Zustellung an Ihre Endpunkte",
      layers: [
        { label: "Quelle", nodes: [{ label: "Ledger", sub: "Buchung" }, { label: "Payment", sub: "Status" }, { label: "Card", sub: "Auth" }] },
        { label: "Event-Bus", nodes: [{ label: "Signatur", sub: "HMAC" }, { label: "Queue", sub: "at-least-once" }, { label: "Retry", sub: "Backoff" }] },
        { label: "Ziel", nodes: [{ label: "Ihr Endpoint", sub: "HTTPS" }, { label: "Replay", sub: "manuell" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/webhook_endpoints",
      code: [
        "// Webhook-Endpoint registrieren",
        "const wh = await cashfux.webhooks.create({",
        '  url: "https://api.ihr-system.de/hooks",',
        '  events: ["payment.settled", "payout.failed"],',
        "});",
        "",
        "// Signatur im Header prüfen:",
        '// "CashFUX-Signature": t=..,v1=..',
      ],
      highlights: [
        "HMAC-Signatur pro Event verifizierbar",
        "At-least-once mit Idempotenz-Header",
        "Automatische Retries plus manuelles Replay",
      ],
    },
    useCases: [
      { title: "Systemsynchronisation", desc: "Interne Systeme in Echtzeit mit dem Ledger abgleichen." },
      { title: "Fulfillment-Trigger", desc: "Lieferung erst nach payment.settled auslösen." },
      { title: "Alerting", desc: "Bei payout.failed sofort reagieren." },
    ],
    faq: [
      { q: "Wie verifiziere ich Webhooks?", a: "Über die HMAC-Signatur im Header, geprüft gegen Ihr Signing-Secret." },
      { q: "Was passiert bei einem Ausfall meines Endpoints?", a: "Zustellungen werden mit exponentiellem Backoff wiederholt und im Event-Log gehalten." },
      { q: "Kann ein Event doppelt ankommen?", a: "Zustellung ist at-least-once; nutzen Sie die Event-ID zur idempotenten Verarbeitung." },
      { q: "Kann ich Events erneut senden?", a: "Ja, einzelne Events lassen sich aus dem Event-Log manuell erneut zustellen." },
    ],
    related: ["apis", "zahlungen", "auszahlungen"],
  },
];
